import React from "react";
import type { FormControlProps, InputProps } from "@mui/joy";
import { FormControl, FormLabel } from "@mui/joy";
import DarkInput from "./DarkInput";
import { colorTokens, ctaAccentColor } from "../../styles/globalStyles";

export interface DarkDatePickerProps extends Omit<InputProps, "type" | "onChange" | "ref"> {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  /** When set, wraps the input in FormControl + FormLabel (same layout as former DarkDatePickerField). */
  label?: React.ReactNode;
  formControlProps?: Omit<FormControlProps, "children" | "required">;
  labelAccent?: boolean;
  labelSize?: "default" | "compact";
  required?: boolean;
  /** Native date input color scheme; use `light` on pale sheets. */
  colorScheme?: "light" | "dark";
}

export default function DarkDatePicker({
  label,
  formControlProps,
  labelAccent = false,
  labelSize = "default",
  required = false,
  colorScheme = "dark",
  value,
  onChange,
  min,
  max,
  sx,
  ...inputProps
}: DarkDatePickerProps) {
  const dateInput = (
    <DarkInput
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{ input: { min, max, style: { colorScheme } } }}
      sx={{
        fontSize: { xs: "0.8rem", sm: "0.875rem" },
        minHeight: { xs: 32, sm: 36 },
        "& input": { color: colorTokens.text.primary },
        ...sx,
      }}
      {...inputProps}
    />
  );

  if (label == null) {
    return dateInput;
  }

  const labelSx = labelAccent
    ? { color: ctaAccentColor, fontSize: { xs: "0.8rem", sm: "0.875rem" } as const }
    : {
        fontSize:
          labelSize === "compact"
            ? ({ xs: "0.7rem", sm: "0.875rem" } as const)
            : ({ xs: "0.8rem", sm: "0.875rem" } as const),
      };

  return (
    <FormControl required={required} {...formControlProps}>
      <FormLabel sx={labelSx}>{label}</FormLabel>
      {dateInput}
    </FormControl>
  );
}
